import { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';

// Componentes
import Breadcrumbs from '../../components/Breadcrumbs/Breadcrumbs';
import FriendlyPhrase from '../../components/FriendlyPhrase/FriendlyPhrase';
import PhoneInput from '../../components/PhoneInput/PhoneInput';

// Contextos
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { useLoader } from '../../contexts/LoaderContext';

// Estilos
import styles from './participanteeditpage.module.css';

// API
import { useParticipanteService } from '../../services/participanteServices';
import { createEmptyPhoneModel, parseCanonicalPhone, phoneModelFromApiValue, toPhonePayload } from '../../utils/phoneCountries';

function ParticipanteEditPage() {
  const { logout } = useAuth();
  const { showToast } = useToast();
  const { showLoader, hideLoader } = useLoader();
  const navigate = useNavigate();
  const { idParticipante } = useParams();
  const { pathname } = useLocation();

  const { obterParticipantePorId, atualizarParticipante } = useParticipanteService();

  const isEditing = !!idParticipante && pathname.includes('atualizar');

  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    celular: createEmptyPhoneModel(),
    senha: '',
  });

  const [idTurmaAtual, setIdTurmaAtual] = useState('');
  const [celularErro, setCelularErro] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isEditing) {
      showToast('Rota de criação não disponível nesta página.', 3000, 'warning');
      return;
    }

    showLoader('Atualizando participante');

    setCelularErro('');
    if (!formData.celular.isValid) {
      const message = 'Celular inválido para o país informado.';
      setCelularErro(message);
      showToast(message, 3000, 'error');
      hideLoader();
      return;
    }

    const participanteJson = {
      id: idParticipante,
      nome: formData.nome,
      email: formData.email,
      ...toPhonePayload('celular', formData.celular),
      loginPor: 'celular',
      senha: formData.senha || '',
    };

    if (idTurmaAtual) {
      participanteJson.idTurma = idTurmaAtual;
    }

    try {
      const response = await atualizarParticipante(participanteJson);

      if (response.success) {
        if (response.celular) {
          setFormData((prev) => ({
            ...prev,
            celular: parseCanonicalPhone(response.celular) || prev.celular,
          }));
        }
        showToast('Participante atualizado com sucesso!', 3000, 'success');
        navigate('/participantes');
      } else {
        const phoneError = response.code === 'invalid_celular'
          ? 'Celular inválido para o país informado.'
          : response.code === 'duplicate_celular'
            ? 'Este celular já está cadastrado.'
            : '';
        if (phoneError) setCelularErro(phoneError);
        showToast(phoneError || response.message || 'Erro ao atualizar participante.', 3000, 'error');
        if (response.status === 401 || response.message?.toLowerCase().includes('token')) logout();
      }
    } catch {
      showToast('Erro de rede ou servidor.', 3000, 'error');
    } finally {
      hideLoader();
    }
  };

  useEffect(() => {
    const carregarDados = async () => {
      showLoader('Carregando dados do participante');

      const participanteResp = await obterParticipantePorId(idParticipante);

      if (participanteResp.success && participanteResp.data) {
        const participante = participanteResp.data;
        setFormData({
          nome: participante.nome || '',
          email: participante.email || '',
          celular: phoneModelFromApiValue(participante.celular),
          senha: '',
        });

        setIdTurmaAtual(participante.idTurma || '');
      } else {
        showToast('Erro ao carregar participante.', 3000, 'error');
        if (participanteResp.message?.toLowerCase().includes('token')) logout();
      }

      hideLoader();
    };

    if (idParticipante) carregarDados();
  }, [idParticipante, isEditing]);

  const breadcrumbLabel = isEditing ? `${formData.nome || 'participante'}` : undefined;

  return (
    <div className={styles.ParticipanteEditPage}>
      <div className={styles.containerTop}>
        <Breadcrumbs idLabels={breadcrumbLabel ? [{ id: idParticipante, label: breadcrumbLabel }] : []} />
        <h1 className={styles.title}>Editar Participante</h1>
        <FriendlyPhrase page="atualizarParticipante" />
      </div>

      <form onSubmit={handleSubmit} className={styles.formulario}>
        <label>
          Nome:
          <input
            type="text"
            name="nome"
            value={formData.nome}
            onChange={handleChange}
            required
          />
        </label>

        <label>
          E-mail:
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
          />
        </label>

        <PhoneInput
          name="celular"
          value={formData.celular}
          onChange={(celular) => setFormData((prev) => ({ ...prev, celular }))}
          label="Celular:"
          error={celularErro}
          required
        />

        <label>
          Senha:
          <input
            type="password"
            name="senha"
            value={formData.senha}
            onChange={handleChange}
            placeholder="Preencha apenas se quiser alterar"
          />
        </label>

        <div className={styles.submitArea}>
          <button type="submit" className={styles.submitButton}>
            Atualizar Participante
          </button>
        </div>
      </form>
    </div>
  );
}

export default ParticipanteEditPage;
